<template>
	<Head>
		<Title>{{ pageTitle }}</Title>
		<Meta name="description" :content="pageTitle" />
		<Meta name="robots" content="noindex, nofollow" />
		<Link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
		<Link rel="icon" type="image/svg+xml" href="/favicon.svg" />
		<Link rel="shortcut icon" href="/favicon.ico" />
		<Link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
		<!-- <Link rel="manifest" href="/site.webmanifest" /> -->
	</Head>
	<div class="page" :class="classes">
		<div class="page-navigation">
			<SitecorePlaceholder name="navigation" :rendering="store.route">
			</SitecorePlaceholder>
		</div>

		<div
			class="page-content"
			:class="{
				'page-content--background': !!background,
			}"
			:style="background"
		>
			<SitecorePlaceholder name="main" :rendering="store.route">
			</SitecorePlaceholder>
		</div>
		<DefaultModal></DefaultModal>
	</div>
</template>

<script setup>
const config = useRuntimeConfig();
const { init: initTracking, pushPageView } = useTracking();
const nuxtApp = useNuxtApp();
const img = useImage();
const { replaceImageSrc } = useImageHelpers();
const { locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const store = useAppStore();
const { session } = useUserSession();
const clarity = useMicrosoftClarity();
const { signIn, signOut } = useMsalStore();

useHead({
	htmlAttrs: {
		lang: locale.value,
	},
});

const routePath = computed(() => {
	let path = route.params.path;
	if (typeof path === "string") {
		if (!path) return "/";
		return path;
	}
	return "/" + path.join("/");
});

await store.fetchRouteData(routePath.value, locale.value).catch((error) => {
	if (error.statusCode === 401) {
		nuxtApp.runWithContext(() => {
			if (session.value?.isFound === false) {
				navigateTo(localePath(config.public.completeProfileUrl), {
					redirectCode: 302,
				});
			} else {
				navigateTo(localePath(config.public.clubCirqueUrl), {
					redirectCode: 302,
				});
			}
		});
	} else {
		showError({
			statusCode: error.statusCode || 500,
			statusMessage: error.message || "Internal Server Error",
			data: {
				route: routePath.value,
				lang: locale.value,
			},
		});
	}
});

onMounted(() => {
	if (route && route.query?.preshow) {
		store.setPreShow(true);
	}
});

const classes = computed(() => store.route?.fields?.pageClass?.value);

const background = computed(() =>
	store.route?.fields?.pageBackgroundImage?.value?.src
		? `background-image: url(${img(
				replaceImageSrc(store.route.fields.pageBackgroundImage.value.src),
				{ quality: 90 }
		  )});`
		: undefined
);

const pageTitle = computed(() => store.route?.fields?.pageTitle?.value || "");
const pageGtm = computed(() => store.route?.fields?.gtm?.value || null);
const isServerSide = computed(() => {
	return (
		store.route?.fields?.renderingMode?.value === "Server" ||
		store.route?.fields?.authGuard?.value === "CC Required" ||
		store.route?.fields?.authGuard?.value === "SSO Required"
	);
});

provide("isServerSide", isServerSide);

if (!import.meta.server) {
	initTracking();
}

onMounted(() => {
	pushPageView(pageGtm.value);
	window.ccSignOut = signOut;
	window.ccSignIn = signIn;
	window.ipEnv = config.public.environment;

	nextTick(() => {
		if (route.hash) {
			const element = document.getElementById(route.hash.substring(1));
			if (element) {
				element.scrollIntoView({ behavior: "smooth" });
			}
		}
	});
});
</script>

<style lang="scss">
.page {
	background: #000;
	width: 100%;
	height: 100vh;
	height: 100dvh;
	top: 0;
	margin: 0 auto;
	position: relative;
	overflow: overlay;

	@media (min-width: 768px) {
		max-width: var(--app-width, 600px);
		min-height: unset;
		height: 80vh;
		top: 10vh;
		border-radius: 8px;

		-ms-overflow-style: none;
		scrollbar-width: none;
		&::-webkit-scrollbar {
			display: none;
		}
	}
}

.page-navigation {
	position: sticky;
	top: 0;
	z-index: 99;
}

.page--homepage {
	background: linear-gradient(to bottom, #000, #191a1a);
}

.page--dark-opacity {
	.page-content {
		padding-top: 200px;
		&:before {
			z-index: 1;
			content: "";
			background: #000;
			opacity: 0.5;
			position: absolute;
			top: 0;
			bottom: 0;
			right: 0;
			left: 0;
			animation: fade-in-half 0.5s ease-in-out;
			animation-fill-mode: forwards;
		}

		.main {
			z-index: 2;
			position: relative;
		}
	}

	.component--pictureimg:has(.picture-img.logo) {
		max-width: 100%;
		margin: 0 auto 16px auto;
		animation: scale-down 0.5s ease-in-out;
		animation-fill-mode: forwards;
	}
}

.page--home {
	.page-content {
		padding-top: 0;
	}
}

.page-content {
	background-size: 100% auto;
	background-position: center top;
	background-repeat: no-repeat;
	min-height: 100vh;
	min-height: 100dvh;
	padding-bottom: 44px;

	@media (min-width: 768px) {
		min-height: 80vh;
		min-height: 80dvh;
	}
}

.page-logo {
	width: 80%;
	position: absolute;
	top: 12vh;
	left: 10%;
	right: 10%;
}

pre {
	color: #fff;
	white-space: pre-wrap;
	background-color: #000;
	overflow: hidden;
	padding: 1rem;
	max-width: 100vw;
	margin: 0;
}
</style>
